import { useTranslation } from 'react-i18next';
import type { EventStreamItem } from '../types/eventMonitor';

function getTypePillStyle(type: string): React.CSSProperties {
  const lower = type.toLowerCase();
  if (lower.includes('user')) {
    return { color: 'var(--color-brand-primary)', borderColor: 'var(--color-brand-primary)' };
  }
  if (lower.includes('login') || lower.includes('auth')) {
    return { color: 'var(--color-info)', borderColor: 'var(--color-info)' };
  }
  if (lower.includes('permission') || lower.includes('security') || lower.includes('role')) {
    return { color: 'var(--color-warning)', borderColor: 'var(--color-warning)' };
  }
  if (lower.includes('config') || lower.includes('code')) {
    return { color: 'var(--color-text-secondary)', borderColor: 'var(--color-surface-border)' };
  }
  return { color: 'var(--color-success)', borderColor: 'var(--color-success)' };
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Seoul',
    });
  } catch {
    return iso;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Seoul',
    });
  } catch {
    return '';
  }
}

function SkeletonRow() {
  return (
    <tr>
      {[30, 50, 45, 35, 25, 60].map((w, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'em-shimmer 1.4s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

interface EventTableProps {
  events: EventStreamItem[];
  newEventIds: Set<string>;
  selectedEventId: string | null;
  isLoading: boolean;
  isPaused: boolean;
  onRowClick: (event: EventStreamItem) => void;
}

/** EventTable — real-time event list (max 1000 rows, sticky header) */
export function EventTable({
  events,
  newEventIds,
  selectedEventId,
  isLoading,
  isPaused,
  onRowClick,
}: EventTableProps) {
  const { t } = useTranslation();

  const tableHeaders = [
    { key: 'time', label: t('eventMonitor.table.time'), width: 140 },
    { key: 'type', label: t('eventMonitor.table.type'), width: 200 },
    { key: 'source', label: t('eventMonitor.table.source'), width: 180 },
    { key: 'user', label: t('eventMonitor.table.user'), width: 140 },
    { key: 'traceId', label: t('eventMonitor.table.traceId'), width: 110 },
    { key: 'payload', label: t('eventMonitor.table.payload'), width: undefined },
  ];

  return (
    <div
      aria-label={t('eventMonitor.table.aria')}
      role="region"
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--color-surface-background)',
        maxHeight: 540,
        overflowY: 'auto',
        position: 'relative',
        opacity: isPaused ? 0.55 : 1,
        filter: isPaused ? 'grayscale(0.6)' : 'none',
        transition: 'opacity 0.2s, filter 0.2s',
      }}
    >
      {/* Paused overlay badge */}
      {isPaused && (
        <div
          aria-label={t('eventMonitor.table.pausedOverlay')}
          style={{
            position: 'absolute',
            top: 'var(--spacing-3)',
            right: 'var(--spacing-3)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            color: 'var(--color-warning)',
            background: 'color-mix(in srgb, var(--color-warning) 18%, var(--color-surface-background))',
            border: '1px solid var(--color-warning)',
            padding: '2px var(--spacing-3)',
            borderRadius: 'var(--radius-full)',
            zIndex: 10,
            pointerEvents: 'none',
          }}
        >
          {t('eventMonitor.table.pausedOverlay')}
        </div>
      )}

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 'var(--font-size-sm)',
        }}
        aria-label={isLoading ? t('eventMonitor.table.ariaLoading') : t('eventMonitor.table.aria')}
        aria-busy={isLoading}
      >
        <thead>
          <tr
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 5,
              background: 'var(--color-surface-elevated)',
            }}
          >
            {tableHeaders.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={{
                  textAlign: 'left',
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 600,
                  borderBottom: '1px solid var(--color-surface-border)',
                  whiteSpace: 'nowrap',
                  fontSize: 'var(--font-size-xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  width: col.width ? col.width : undefined,
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}

          {!isLoading &&
            events.map((event) => {
              const isNew = newEventIds.has(event.eventId);
              const isSelected = selectedEventId === event.eventId;

              return (
                <tr
                  key={event.eventId}
                  tabIndex={0}
                  role="button"
                  aria-label={t('eventMonitor.table.rowDetail', { eventId: event.eventId })}
                  aria-pressed={isSelected}
                  onClick={() => onRowClick(event)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick(event);
                    }
                  }}
                  style={{
                    cursor: 'pointer',
                    background: isSelected
                      ? 'color-mix(in srgb, var(--color-brand-primary) 12%, transparent)'
                      : isNew
                      ? 'color-mix(in srgb, var(--color-success) 14%, transparent)'
                      : 'transparent',
                    animation: isNew ? 'em-fade-new 1.2s ease-out forwards' : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      (e.currentTarget as HTMLTableRowElement).style.background =
                        'var(--color-surface-elevated)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      (e.currentTarget as HTMLTableRowElement).style.background = isNew
                        ? 'color-mix(in srgb, var(--color-success) 14%, transparent)'
                        : 'transparent';
                    }
                  }}
                >
                  {/* Time */}
                  <td
                    style={{
                      padding: 'var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-primary)',
                          fontWeight: 600,
                        }}
                      >
                        {formatTime(event.occurredAt)}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 10,
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {formatDate(event.occurredAt)}
                      </span>
                    </div>
                  </td>

                  {/* Type */}
                  <td
                    style={{
                      padding: 'var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        padding: '1px var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid',
                        ...getTypePillStyle(event.type),
                      }}
                    >
                      {event.type}
                    </span>
                  </td>

                  {/* Source */}
                  <td
                    style={{
                      padding: 'var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      {event.source}
                    </span>
                  </td>

                  {/* User */}
                  <td
                    style={{
                      padding: 'var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      {event.userId ?? (
                        <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                          {t('eventMonitor.table.system')}
                        </span>
                      )}
                    </span>
                  </td>

                  {/* Trace ID */}
                  <td
                    style={{
                      padding: 'var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 10,
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      {event.traceId ? `${event.traceId.slice(0, 8)}...` : '-'}
                    </span>
                  </td>

                  {/* Payload */}
                  <td
                    style={{
                      padding: 'var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        display: 'block',
                        maxWidth: 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {event.payloadSummary ?? '-'}
                    </span>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>

      <style>{`
        @keyframes em-shimmer {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
        @keyframes em-fade-new {
          0%   { background: color-mix(in srgb, var(--color-success) 32%, transparent); }
          100% { background: transparent; }
        }
        @media (max-width: 768px) {
          [aria-label="이벤트 목록"] table,
          [aria-label="Event list"] table { min-width: 640px; }
        }
      `}</style>
    </div>
  );
}
